import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { useOrgSnapshot } from "../../../hooks/useOrgSnapshot";
import { Pools } from "../../../lib/apiTyped";
import { useResource } from "../../../lib/useResource";
import { PageHeader } from "../../../components/AppLayout";
import { Card } from "../../../components/shared/Card";
import { Spinner } from "../../../components/shared/Spinner";
import { fmtBytes, fmtDate, fmtNumber } from "../../../lib/format";

// Refresh cadence for the pool snapshot. The doc updates on every usage
// event; 15s feels live enough without hammering the API for a screen
// the user usually leaves in the background.
const POOL_REFRESH_MS = 15_000;

export function OwnerUsage() {
  const { orgId, role } = useOrgSnapshot();
  const [pricingDisplayNames, setPricingDisplayNames] = useState<
    Record<string, string>
  >({});

  // /pool/me is the single source of truth: backend filters the pool
  // doc to fields a member is allowed to see (limits, totals, killSwitch,
  // own period usage incl. byModel). Reading pools/{orgId} directly from
  // Firestore is restricted to admin+ by rules; using the endpoint keeps
  // members out of permission-denied loops.
  const { data, loading, refetch } = useResource(
    () => (orgId ? Pools.me(orgId) : Promise.resolve(null)),
    [orgId],
  );

  useEffect(() => {
    if (!orgId) return;
    const t = setInterval(() => void refetch(), POOL_REFRESH_MS);
    return () => clearInterval(t);
  }, [orgId, refetch]);

  useEffect(() => {
    if (!db) return;
    let cancelled = false;
    void (async () => {
      const snap = await getDocs(collection(db, "modelPricing"));
      if (cancelled) return;
      const map: Record<string, string> = {};
      snap.docs.forEach((d) => {
        const data = d.data() as { displayName?: string };
        if (data.displayName) map[d.id] = data.displayName;
      });
      setPricingDisplayNames(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  const isExternal = role === "external";

  // Two clocks, never mixed in one ratio:
  //   - Credit window: the pool counter and the caller's slice of it. Both
  //     reset at each payment/renewal, so both are comparable with the pool
  //     limit — that's what the bars show.
  //   - Calendar month: the `usage/{poolId}/periods/{YYYY-MM}` mirror, which
  //     never resets. Only the by-model breakdown reads it, and that card
  //     computes its percentages against its own monthly total.
  const poolCreditsUsed = data.creditsUsedThisMonth ?? 0;
  const personalCreditsUsed = data.creditsUsedThisWindow ?? 0;
  const poolCreditsLimit = data.monthlyCreditsLimit ?? 0;
  const pctOfPool = (credits: number) =>
    poolCreditsLimit > 0 ? Math.min(100, (credits / poolCreditsLimit) * 100) : 0;
  const windowStartLabel = data.creditWindowStartMs
    ? fmtDate(data.creditWindowStartMs)
    : null;

  const memberUsage = data.memberUsage ?? {};
  const monthlyCreditsUsed = memberUsage.creditsUsed ?? 0;
  const breakdown = Object.entries(memberUsage.byModel ?? {})
    .map(([modelId, entry]) => ({
      modelId,
      displayName:
        entry.displayName ?? pricingDisplayNames[modelId] ?? modelId,
      credits: entry.credits ?? 0,
      requests: entry.requests ?? 0,
    }))
    .filter((row) => row.credits > 0)
    .sort((a, b) => b.credits - a.credits);

  return (
    <>
      <PageHeader
        title="Usage"
        subtitle={
          (isExternal
            ? "Tu consumo personal en este periodo. Solo ves lo tuyo."
            : "Pool compartido de la organización. Tu consumo personal cuenta contra el pool, no contra una cuota individual.") +
          (windowStartLabel
            ? ` El periodo de créditos empezó el ${windowStartLabel} y se reinicia en cada renovación.`
            : "")
        }
      />
      {data.killSwitch && (
        <Card className="p-5 mb-4 border-red-200 bg-red-50">
          <div className="text-sm font-semibold text-red-700">
            Pool de créditos agotado
          </div>
          <p className="text-xs text-red-700 mt-1">
            La organización ha alcanzado el límite mensual. Las llamadas al
            modelo están bloqueadas hasta que el owner amplíe el plan o
            active el cobro por exceso.
          </p>
        </Card>
      )}
      {data.storageFull && !isExternal && (
        <Card className="p-5 mb-4 border-amber-200 bg-amber-50">
          <div className="text-sm font-semibold text-amber-800">
            Almacenamiento al límite
          </div>
          <p className="text-xs text-amber-800 mt-1">
            La organización ha alcanzado el límite de almacenamiento
            compartido. Borra archivos antiguos o aumenta el plan para
            seguir subiendo.
          </p>
        </Card>
      )}

      {!isExternal && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-4">
          <UsageBar
            title="Pool de la organización"
            pct={pctOfPool(poolCreditsUsed)}
            usedLabel={`${fmtNumber(poolCreditsUsed)} / ${fmtNumber(poolCreditsLimit)} créditos en este periodo`}
          />
          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
              Storage compartido
            </div>
            <div className="mt-2 text-2xl font-black text-ink">
              {fmtBytes(data.storageBytesUsed ?? 0)}
            </div>
            <div className="text-xs text-ink-3 mt-0.5">
              {data.storageBytesLimit
                ? `de ${fmtBytes(data.storageBytesLimit)}`
                : "sin límite"}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
              Rate limit
            </div>
            <div className="mt-2 text-2xl font-black text-ink">
              {fmtNumber(data.rpmLimit ?? 0)}
            </div>
            <div className="text-xs text-ink-3 mt-0.5">requests / minuto por miembro</div>
          </Card>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <UsageBar
          title="Tu consumo en este periodo"
          pct={pctOfPool(personalCreditsUsed)}
          usedLabel={`${fmtNumber(personalCreditsUsed)} de los ${fmtNumber(poolCreditsLimit)} créditos del pool`}
          accent="ink"
        />
        <Card className="p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Política de exceso
          </div>
          <div className="mt-2 text-2xl font-black text-ink">
            {data.overagePolicy === "billed" ? "Cobrado" : "Bloqueado"}
          </div>
          <div className="text-xs text-ink-3 mt-0.5">
            {data.overagePolicy === "billed"
              ? `Si el pool se agota, el exceso se factura a ${data.overageEurPerMillionCredits ?? 0} € por millón de créditos.`
              : "Si el pool se agota, el servicio se corta hasta el siguiente periodo o ampliación."}
          </div>
        </Card>
      </div>

      {breakdown.length > 0 && (
        <Card className="p-5 mt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Tu consumo por modelo · mes natural
          </div>
          {/* Distinto reloj que las barras de arriba: este desglose acumula el
              mes de calendario completo, así que sus porcentajes se calculan
              sobre su propio total y no sobre el límite del pool. */}
          <div className="text-xs text-ink-3 mt-0.5 mb-3">
            {fmtNumber(monthlyCreditsUsed)} créditos en {data.period}, incluidos
            los anteriores a la última renovación.
          </div>
          <div className="space-y-2">
            {breakdown.map((row) => {
              const pct =
                monthlyCreditsUsed > 0
                  ? (row.credits / monthlyCreditsUsed) * 100
                  : 0;
              return (
                <div key={row.modelId}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ink">{row.displayName}</span>
                    <span className="text-ink-3">
                      {pct.toFixed(1)}%
                      <span className="ml-2 text-xs">({row.requests} req)</span>
                    </span>
                  </div>
                  <div
                    className="mt-1 h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
                  >
                    <div
                      className="h-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: "#0f0f0f" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </>
  );
}

function UsageBar({
  title,
  pct,
  usedLabel,
  accent = "danger",
}: {
  title: string;
  pct: number;
  usedLabel: string;
  accent?: "danger" | "ink";
}) {
  const danger = accent === "danger" && pct > 90;
  return (
    <Card className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
        {title}
      </div>
      <div className="mt-2 text-2xl font-black text-ink">{pct.toFixed(1)}%</div>
      <div className="text-xs text-ink-3 mt-0.5">{usedLabel}</div>
      <div
        className="mt-3 h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
      >
        <div
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            backgroundColor: danger ? "#C61418" : "#0f0f0f",
          }}
        />
      </div>
    </Card>
  );
}
